import { Component, signal, OnInit, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CoursesService, CursoCompleto } from '../../services/courses.service';
import { NotificationService } from '../../services/notification.service';
import { environment } from '../../../environments/environment.development';

interface Course {
  id: number;
  title: string;
  instructor: string;
  thumbnail: string;
  rating: number;
  reviews: number;
  students: number;
  price: number;
  originalPrice?: number;
  duration: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  category: string;
  tags: string[];
  isBestseller: boolean;
  isNew: boolean;
  lastUpdated: string;
  es_premium: boolean;
  total_clases: number;
  total_modulos: number;
}

interface Category {
  id: string;
  name: string;
  icon: string;
  count: number;
}

@Component({
  selector: 'app-courses-list',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './courses-list.html',
  styleUrl: './courses-list.css',
})
export class CoursesListComponent implements OnInit {
  private coursesService = inject(CoursesService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);

  searchQuery = signal('');
  selectedCategory = signal<string>('all');
  selectedLevel = signal<string>('all');
  sortBy = signal<string>('popular');
  showFilters = signal(false);
  isLoading = signal(false);

  // Cursos reales del backend
  cursosReales = signal<Course[]>([]);

  categories: Category[] = [
    { id: 'all', name: 'Todos los Cursos', icon: '📚', count: 0 },
    { id: 'premium', name: 'Cursos Premium', icon: '⭐', count: 0 },
    { id: 'free', name: 'Cursos Gratuitos', icon: '🎁', count: 0 },
  ];

  ngOnInit() {
    window.scrollTo(0, 0);
    this.loadCursos();
  }

  /**
   * Carga todos los cursos disponibles
   */
  loadCursos() {
    this.isLoading.set(true);

    this.coursesService.getAllCursos().subscribe({
      next: (response) => {
        this.isLoading.set(false);

        if (response.ok && response.data) {
          // Mapear cursos del backend al formato del componente
          const cursosMapeados = response.data.map(this.mapCursoBackendToComponent.bind(this));
          this.cursosReales.set(cursosMapeados);

          // Actualizar contador de categorías
          this.updateCategoryCounts(cursosMapeados);

          console.log(`✅ ${response.data.length} cursos cargados exitosamente`);
        } else {
          this.notificationService.error(
            'Error',
            response.msg || 'No se pudieron cargar los cursos'
          );
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        console.error('❌ Error al cargar cursos:', error);
        this.notificationService.error(
          'Error de Conexión',
          'No se pudo conectar con el servidor'
        );
      }
    });
  }

  /**
   * Mapea un curso del backend al formato del componente
   */
  private mapCursoBackendToComponent(curso: CursoCompleto): Course {
    // Construir URL de imagen desde Google Cloud Storage
    const imageUrl = curso.foto_portada
      ? `https://storage.googleapis.com/traidinglab/cursos/portadas/${curso.foto_portada}`
      : 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=225&fit=crop';

    // Calcular duración en formato legible
    const hours = Math.floor(curso.duracion_minutos / 60);
    const minutes = curso.duracion_minutos % 60;
    const duration = hours > 0 
      ? `${hours}h ${minutes > 0 ? minutes + 'min' : ''}`
      : `${minutes}min`;

    // Construir tags
    const tags: string[] = [];
    if (curso.es_premium) {
      tags.push('Premium');
    }
    tags.push(`${curso.total_clases} clases`);
    tags.push(`${curso.modulos.length} módulos`);

    // Obtener precio (del primer plan si existe)
    const precio = curso.planes.length > 0 
      ? parseFloat(curso.planes[0].precio) 
      : 0;

    // Determinar categoría basada en es_premium
    const category = curso.es_premium ? 'premium' : 'free';

    // Fecha de última actualización
    const fechaCreacion = new Date(curso.fecha_creacion);
    const lastUpdated = fechaCreacion.toLocaleDateString('es-ES', {
      month: 'long',
      year: 'numeric'
    });

    return {
      id: curso.id_curso,
      title: curso.titulo,
      instructor: curso.maestro ? `${curso.maestro.nombre} ${curso.maestro.apellido}`.trim() : 'Instructor no disponible',
      thumbnail: imageUrl,
      rating: 4.5, // Por ahora fijo, luego puede venir del backend
      reviews: 0,
      students: 0,
      price: precio,
      originalPrice: precio > 0 ? precio * 1.5 : undefined,
      duration: duration,
      level: 'beginner', // Por ahora fijo, luego puede venir del backend
      category: category,
      tags: tags,
      isBestseller: false,
      isNew: this.isRecentCourse(curso.fecha_creacion),
      lastUpdated: lastUpdated,
      es_premium: curso.es_premium,
      total_clases: curso.total_clases,
      total_modulos: curso.modulos.length
    };
  }

  /**
   * Determina si un curso es reciente (últimos 30 días)
   */
  private isRecentCourse(fechaCreacion: string): boolean {
    const fecha = new Date(fechaCreacion);
    const hoy = new Date();
    const diffTime = Math.abs(hoy.getTime() - fecha.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30;
  }

  /**
   * Actualiza el contador de cursos por categoría
   */
  private updateCategoryCounts(cursos: Course[]) {
    const allCount = cursos.length;
    const premiumCount = cursos.filter(c => c.es_premium).length;
    const freeCount = cursos.filter(c => !c.es_premium).length;

    this.categories = [
      { id: 'all', name: 'Todos los Cursos', icon: '📚', count: allCount },
      { id: 'premium', name: 'Cursos Premium', icon: '⭐', count: premiumCount },
      { id: 'free', name: 'Cursos Gratuitos', icon: '🎁', count: freeCount },
    ];
  }

  getFilteredCourses(): Course[] {
    let filtered = [...this.cursosReales()];

    // Filter by category
    if (this.selectedCategory() === 'premium') {
      filtered = filtered.filter(course => course.es_premium);
    } else if (this.selectedCategory() === 'free') {
      filtered = filtered.filter(course => !course.es_premium);
    }

    // Filter by level
    if (this.selectedLevel() !== 'all') {
      filtered = filtered.filter(course => course.level === this.selectedLevel());
    }

    // Filter by search query
    if (this.searchQuery().trim()) {
      const query = this.searchQuery().toLowerCase();
      filtered = filtered.filter(course => 
        course.title.toLowerCase().includes(query) ||
        course.instructor.toLowerCase().includes(query) ||
        course.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Sort
    switch (this.sortBy()) {
      case 'popular':
        filtered.sort((a, b) => b.students - a.students);
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        filtered.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
        break;
    }

    return filtered;
  }

  selectCategory(categoryId: string) {
    this.selectedCategory.set(categoryId);
  }

  toggleFilters() {
    this.showFilters.set(!this.showFilters());
  }

  clearFilters() {
    this.selectedCategory.set('all');
    this.selectedLevel.set('all');
    this.searchQuery.set('');
    this.sortBy.set('popular');
  }

  goToCourse(courseId: number) {
    this.router.navigate(['/course', courseId]);
  }

  formatNumber(num: number): string {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  }

  getDiscount(course: Course): number {
    if (!course.originalPrice) return 0;
    return Math.round(((course.originalPrice - course.price) / course.originalPrice) * 100);
  }

  getLevelLabel(level: string): string {
    const labels: { [key: string]: string } = {
      'beginner': 'Principiante',
      'intermediate': 'Intermedio',
      'advanced': 'Avanzado'
    };
    return labels[level] || level;
  }

  getLevelColor(level: string): string {
    const colors: { [key: string]: string } = {
      'beginner': '#34a853',
      'intermediate': '#fbbc04',
      'advanced': '#ea4335'
    };
    return colors[level] || '#5f6368';
  }
}

